import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Tv, Layers, Battery, Gauge, Package, TrendingUp } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import bellabotProImg from "@/assets/robots/bellabot-pro.jpg";
import bellabotProVideo from "@/assets/robots/bellabot-pro-demo.mp4";

const BellaBotPro = () => {
  const [showVideo, setShowVideo] = useState(false);

  const specifications = [
    {
      icon: <Tv className="w-5 h-5" />,
      label: "Ad Screen",
      value: "18.5-inch Display"
    },
    {
      icon: <Layers className="w-5 h-5" />,
      label: "Tray Capacity",
      value: "4 Smart Trays"
    },
    {
      icon: <Battery className="w-5 h-5" />,
      label: "Battery Life",
      value: "Extended Runtime"
    },
    {
      icon: <Gauge className="w-5 h-5" />,
      label: "Mobility",
      value: "52cm Clearance"
    }
  ];

  const keyFeatures = [
    {
      icon: <Tv className="w-6 h-6" />,
      title: "18.5-inch Advertising Screen",
      description: "Large high-resolution display for promotional content, menu displays, and interactive advertising to boost revenue while delivering exceptional service."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Smart Trays Detection",
      description: "Advanced sensors automatically detect when items are placed or removed from trays, ensuring accurate delivery tracking and customer notifications."
    },
    {
      icon: <Gauge className="w-6 h-6" />,
      title: "52cm Superior Mobility",
      description: "Compact design with 52cm minimum passage width enables navigation through crowded restaurants and tight spaces with ease."
    },
    {
      icon: <Battery className="w-6 h-6" />,
      title: "Extended Battery Life",
      description: "Enhanced battery system provides longer operating hours to handle peak service times without interruption, with fast charging capabilities."
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Multi-Modal Interaction",
      description: "Engaging customer experience with voice feedback, LED expressions, and responsive animations that delight guests of all ages."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Revenue Enhancement",
      description: "Generate additional income through advertising display while improving operational efficiency and reducing labor costs by up to 50%."
    }
  ];

  const applications = [
    {
      title: "Full-Service Restaurants",
      description: "Food and beverage delivery from kitchen to tables, dirty dish collection, and continuous promotional content display to increase upsells."
    },
    {
      title: "Quick Service Restaurants",
      description: "Order delivery to tables, promotional campaigns on display screen, and queue management to improve customer flow and satisfaction."
    },
    {
      title: "Hotels & Resorts",
      description: "Room service delivery, amenity distribution, and property advertising on the display screen to promote facilities and services."
    },
    {
      title: "Cafés & Coffee Shops",
      description: "Beverage delivery, table clearing, and menu item promotions to drive sales while maintaining efficient operations."
    },
    {
      title: "Buffets & Banquet Halls",
      description: "Dish replenishment, dirty dish collection, and event promotion through the advertising display to enhance guest experience."
    },
    {
      title: "Entertainment Venues",
      description: "Concession delivery, promotional advertising, and service enhancement in theaters, arcades, and entertainment centers."
    }
  ];

  return (
    <>
      <SEO 
        title="BellaBot Pro - Premium Delivery & Advertising Robot | Neil AI"
        description="Enhanced delivery robot with 18.5-inch advertising screen, smart tray detection, and extended battery life. Perfect for restaurants, hotels, and hospitality venues."
        keywords="BellaBot Pro, delivery robot, advertising robot, restaurant robot, hospitality automation, service robot"
      />
      <Navigation />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 pt-12 pb-8 md:pt-16">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Robots
            </Link>
          </Button>

          {/* Hero Section */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="relative bg-white/5 rounded-lg p-8 flex items-center justify-center">
              {showVideo ? (
                <video 
                  src={bellabotProVideo}
                  controls
                  autoPlay
                  loop
                  className="w-full h-auto max-h-[600px] rounded-lg"
                />
              ) : (
                <img 
                  src={bellabotProImg} 
                  alt="BellaBot Pro - Premium Delivery & Advertising Robot" 
                  className="w-full h-auto max-h-[600px] object-contain"
                />
              )}
              <Badge className="absolute top-4 right-4 bg-secondary text-secondary-foreground font-bold">
                NEW
              </Badge>
            </div>

            <div className="flex flex-col justify-center space-y-6">
              <div>
                <p className="text-primary font-semibold mb-2">Premium Delivery & Advertising Robot</p>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">BellaBot Pro</h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 leading-relaxed">
                  Enhanced delivery robot featuring an 18.5-inch advertising screen, smart tray detection, and extended battery life for superior hospitality service.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <p className="text-xs font-semibold">{spec.label}</p>
                      </div>
                      <p className="text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant={showVideo ? "outline" : "secondary"}
                  className="flex-1"
                  onClick={() => setShowVideo(!showVideo)}
                >
                  {showVideo ? "View Image" : "Watch Demo"}
                </Button>
                <Button 
                  asChild 
                  className="flex-1 glow-gold"
                >
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="flex-1"
                >
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specifications">Specifications</TabsTrigger>
              <TabsTrigger value="applications">Applications</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Dual-Purpose Innovation</CardTitle>
                  <CardDescription>
                    Deliver service excellence while generating additional revenue through advertising
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    BellaBot Pro takes delivery robotics to the next level by combining premium service capabilities with integrated advertising. The 18.5-inch high-resolution display transforms your robot into a mobile marketing platform, showcasing promotions, menu items, and special offers while delivering food and beverages with precision.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Smart tray detection technology uses advanced sensors to automatically detect when items are loaded or removed, providing real-time delivery status and ensuring accountability. The system integrates seamlessly with your restaurant management system for streamlined operations.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    With 52cm superior mobility, BellaBot Pro navigates crowded dining rooms and tight spaces with ease. The extended battery life ensures uninterrupted service during your busiest hours, while the charming personality with expressive animations creates memorable guest experiences.
                  </p>
                </CardContent>
              </Card>

              <div>
                <h2 className="font-orbitron text-2xl font-bold mb-6">Key Features</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {keyFeatures.map((feature, idx) => (
                    <Card key={idx} className="glass-effect border-[hsl(var(--line))] hover:scale-105 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="text-primary mb-3">{feature.icon}</div>
                        <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8">
                  <h3 className="font-orbitron text-xl font-bold mb-4">ROI Impact</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">50%</p>
                      <p className="text-sm">Labor Cost Reduction</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">300+</p>
                      <p className="text-sm">Trips Per Day</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">15%</p>
                      <p className="text-sm">Revenue Increase via Ads</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specifications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Technical Specifications</CardTitle>
                  <CardDescription>Detailed specifications of BellaBot Pro</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Display & Interface</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Screen Size:</span> 18.5-inch HD Display</p>
                          <p className="text-sm"><span className="font-semibold">Resolution:</span> High-definition advertising quality</p>
                          <p className="text-sm"><span className="font-semibold">Content Management:</span> Cloud-based system</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Interaction:</span> Multi-modal (voice, touch, gesture)</p>
                          <p className="text-sm"><span className="font-semibold">Expressions:</span> LED emotional display</p>
                          <p className="text-sm"><span className="font-semibold">Languages:</span> Multi-language support</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Delivery System</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Trays:</span> 4 smart detection trays</p>
                          <p className="text-sm"><span className="font-semibold">Payload:</span> Up to 40kg total capacity</p>
                          <p className="text-sm"><span className="font-semibold">Detection:</span> 360° fisheye lens monitoring</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Tray Sensors:</span> Automatic load/unload detection</p>
                          <p className="text-sm"><span className="font-semibold">Height:</span> Adjustable tray configuration</p>
                          <p className="text-sm"><span className="font-semibold">Material:</span> Food-grade surfaces</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Navigation & Performance</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Navigation:</span> PUDU VSLAM+ technology</p>
                          <p className="text-sm"><span className="font-semibold">Mobility:</span> 52cm minimum passage width</p>
                          <p className="text-sm"><span className="font-semibold">Speed:</span> Adaptive speed control</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Obstacle Avoidance:</span> 3D depth sensing</p>
                          <p className="text-sm"><span className="font-semibold">Battery:</span> Extended life, fast charging</p>
                          <p className="text-sm"><span className="font-semibold">Operation:</span> 10-12 hours per charge</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Industry Applications</CardTitle>
                  <CardDescription>
                    Transform service delivery across hospitality venues
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="space-y-2 p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                        <h3 className="font-bold text-lg text-primary">{app.title}</h3>
                        <p className="text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8 text-center">
                  <h3 className="font-orbitron text-2xl font-bold mb-4">Ready to Elevate Your Service?</h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                    Join thousands of venues worldwide using BellaBot Pro to deliver exceptional service while generating additional revenue through advertising.
                  </p>
                  <Button asChild size="lg" className="glow-gold">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Schedule Your Demo Today
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default BellaBotPro;
